import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import {
  ArrowLeft,
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Calendar,
  CreditCard,
  Send,
  Clock,
  DollarSign
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.4 }
};

export default function SubscriptionsPage() {
  const { t } = useTranslation();
  const { user, token } = useAuth();
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [pendingOrders, setPendingOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProfiles, setSelectedProfiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [renewalSuccess, setRenewalSuccess] = useState(null);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      const [subsRes, ordersRes] = await Promise.all([
        fetch(`${API}/owner/subscriptions`, { headers }),
        fetch(`${API}/owner/pending-orders`, { headers })
      ]);

      if (subsRes.ok) {
        const data = await subsRes.json();
        setSubscriptionData(data);
      }

      if (ordersRes.ok) {
        const orders = await ordersRes.json();
        setPendingOrders(orders.filter(o => o.type === 'renewal'));
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error(t('errors.serverError'));
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscriptions = async () => {
    try {
      const response = await fetch(`${API}/owner/subscriptions`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        
      });

      if (response.ok) {
        const data = await response.json();
        setSubscriptionData(data);
        // Pre-select expired profiles
        const expiredIds = data.expired_profiles?.map(p => p.profile_id) || [];
        setSelectedProfiles(expiredIds);
      }
    } catch (error) {
      console.error('Failed to fetch subscriptions:', error);
      toast.error(t('errors.serverError'));
    } finally {
      setLoading(false);
    }
  };

  const toggleProfile = (profileId) => {
    setSelectedProfiles(prev => {
      if (prev.includes(profileId)) {
        return prev.filter(id => id !== profileId);
      } else {
        return [...prev, profileId];
      }
    });
  };

  const selectAllExpired = () => {
    const expiredIds = subscriptionData?.expired_profiles?.map(p => p.profile_id) || [];
    setSelectedProfiles(expiredIds);
  };

  const selectAllExpiringSoon = () => {
    const soonIds = subscriptionData?.expiring_soon_profiles?.map(p => p.profile_id) || [];
    setSelectedProfiles(prev => [...new Set([...prev, ...soonIds])]);
  };

  const selectAll = () => {
    const allIds = [
      ...(subscriptionData?.active_profiles?.map(p => p.profile_id) || []),
      ...(subscriptionData?.expired_profiles?.map(p => p.profile_id) || []),
      ...(subscriptionData?.expiring_soon_profiles?.map(p => p.profile_id) || [])
    ];
    setSelectedProfiles([...new Set(allIds)]);
  };

  const clearSelection = () => {
    setSelectedProfiles([]);
  };

  const calculateTotal = () => {
    if (!subscriptionData) return 0;
    return selectedProfiles.length * subscriptionData.renewal_cost_per_profile;
  };

  const handleRenew = async () => {
    if (selectedProfiles.length === 0) {
      toast.error(t('subscriptions.selectProfiles'));
      return;
    }

    setSubmitting(true);
    try {
      // Try Stripe payment first
      const stripeResponse = await fetch(`${API}/checkout/stripe/renewal`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        
        body: JSON.stringify({
          profile_ids: selectedProfiles,
          origin_url: window.location.origin
        })
      });

      if (stripeResponse.ok) {
        const stripeData = await stripeResponse.json();
        // Redirect to Stripe Checkout
        window.location.href = stripeData.checkout_url;
        return;
      }

      // Fallback to manual renewal if Stripe fails
      console.warn('Stripe not available, falling back to manual renewal');
      const response = await fetch(`${API}/owner/subscriptions/renew`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        
        body: JSON.stringify(selectedProfiles)
      });

      if (response.ok) {
        const data = await response.json();
        setRenewalSuccess({
          orderId: data.order_id,
          profiles: selectedProfiles,
          total: data.total
        });
        toast.success(t('subscriptions.renewalSubmitted'));
        setSelectedProfiles([]);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || t('errors.serverError'));
      }
    } catch (error) {
      toast.error(t('errors.serverError'));
    } finally {
      setSubmitting(false);
    }
  };

  const getProfileStatus = (profile) => {
    if (!profile.subscription_expires) return 'expired';
    const expiryDate = new Date(profile.subscription_expires);
    const now = new Date();
    const daysUntilExpiry = Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24));
    
    if (daysUntilExpiry <= 0) return 'expired';
    if (daysUntilExpiry <= 15) return 'expiring';
    return 'active';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  const getDaysUntilExpiry = (dateStr) => {
    if (!dateStr) return 0;
    const expiryDate = new Date(dateStr);
    const now = new Date();
    return Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24));
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  const allProfiles = [
    ...(subscriptionData?.expired_profiles || []).map(p => ({ ...p, _status: 'expired' })),
    ...(subscriptionData?.expiring_soon_profiles || []).map(p => ({ ...p, _status: 'expiring' })),
    ...(subscriptionData?.active_profiles?.filter(
      p => !subscriptionData?.expiring_soon_profiles?.find(e => e.profile_id === p.profile_id)
    ) || []).map(p => ({ ...p, _status: 'active' }))
  ];

  return (
    <div className="min-h-screen pt-24 pb-16 bg-slate-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div {...fadeUp} className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            <Link to="/owner">
              <Button variant="ghost" size="sm" className="gap-2">
                <ArrowLeft className="w-4 h-4" />
                {t('common.back')}
              </Button>
            </Link>
          </div>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="font-heading text-3xl font-bold text-slate-900 mb-2">
                {t('subscriptions.title')}
              </h1>
              <p className="text-slate-600">
                {t('subscriptions.subtitle')}
              </p>
            </div>
            <Button variant="outline" onClick={fetchData} className="gap-2" data-testid="refresh-btn">
              <RefreshCw className="w-4 h-4" />
              {t('common.refresh')}
            </Button>
          </div>
        </motion.div>

        {/* Pending Orders Alert */}
        {pendingOrders.length > 0 && (
          <motion.div {...fadeUp} className="mb-8">
            <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Clock className="w-6 h-6 text-yellow-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-heading text-lg font-semibold text-yellow-800 mb-2">
                    {t('subscriptions.pendingPayments')}
                  </h3>
                  {pendingOrders.map((order) => (
                    <div key={order.order_id} className="bg-white rounded-xl p-4 mb-3 border border-yellow-200">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-mono text-sm font-bold text-slate-600">{order.order_id}</span>
                        <span className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-sm font-medium">
                          {t('subscriptions.awaitingPayment')}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-600">
                          {order.quantity} {t('subscriptions.profilesLabel')}
                        </span>
                        <span className="font-bold text-slate-900">${order.total}</span>
                      </div>
                      <div className="mt-2 text-xs text-slate-500">
                        {t('subscriptions.profilesConcerned')}: {order.profile_ids?.join(', ')}
                      </div>
                      <div className="mt-2 text-xs text-slate-500">
                        {t('subscriptions.createdAt')}: {formatDate(order.created_at)}
                      </div>
                    </div>
                  ))}
                  <p className="text-sm text-yellow-700 mt-2">
                    {t('subscriptions.contactForPayment')}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Renewal Success Message */}
        {renewalSuccess && (
          <motion.div {...fadeUp} className="mb-8">
            <div className="bg-[#0A2540]/5 border border-[#E5E7EB] rounded-2xl p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#0A2540]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Send className="w-6 h-6 text-[#0A2540]" />
                </div>
                <div>
                  <h3 className="font-heading text-lg font-semibold text-blue-800 mb-2">
                    {t('subscriptions.renewalRequestSent')}
                  </h3>
                  <p className="text-sm text-[#0A2540] mb-2">
                    {t('subscriptions.orderId')}: <span className="font-mono font-bold">{renewalSuccess.orderId}</span>
                  </p>
                  <p className="text-sm text-[#0A2540] mb-2">
                    {t('subscriptions.profilesConcerned')}: {renewalSuccess.profiles.join(', ')}
                  </p>
                  <p className="text-sm text-[#0A2540]">
                    {t('subscriptions.total')}: <span className="font-bold">${renewalSuccess.total}</span>
                  </p>
                  <p className="text-sm text-[#0A2540] mt-3">
                    {t('subscriptions.awaitingAdminValidation')}
                  </p>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="mt-2 text-[#0A2540]"
                    onClick={() => setRenewalSuccess(null)}
                  >
                    {t('common.dismiss')}
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <motion.div {...fadeUp} className="bg-white rounded-2xl p-5 shadow-soft">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-[#00C48C]/10 rounded-xl flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-[#00C48C]" />
              </div>
            </div>
            <p className="text-2xl font-bold text-[#00C48C]">
              {subscriptionData?.active_profiles?.length || 0}
            </p>
            <p className="text-sm text-slate-500">{t('subscriptions.activeProfiles')}</p>
          </motion.div>

          <motion.div {...fadeUp} transition={{ delay: 0.1 }} className="bg-white rounded-2xl p-5 shadow-soft">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-yellow-100 rounded-xl flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-yellow-600" />
              </div>
            </div>
            <p className="text-2xl font-bold text-yellow-600">
              {subscriptionData?.expiring_soon_profiles?.length || 0}
            </p>
            <p className="text-sm text-slate-500">{t('subscriptions.expiringSoon')}</p>
          </motion.div>

          <motion.div {...fadeUp} transition={{ delay: 0.2 }} className="bg-white rounded-2xl p-5 shadow-soft">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
                <XCircle className="w-5 h-5 text-red-600" />
              </div>
            </div>
            <p className="text-2xl font-bold text-red-600">
              {subscriptionData?.expired_profiles?.length || 0}
            </p>
            <p className="text-sm text-slate-500">{t('subscriptions.expiredProfiles')}</p>
          </motion.div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Profiles List */}
          <div className="lg:col-span-2">
            <motion.div {...fadeUp} className="bg-white rounded-2xl shadow-soft">
              <div className="p-6 border-b border-slate-100">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <h2 className="font-heading text-xl font-semibold text-slate-900">
                    {t('subscriptions.selectProfiles')}
                  </h2>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={selectAllExpired} className="text-xs">
                      {t('subscriptions.selectExpired')}
                    </Button>
                    <Button size="sm" variant="outline" onClick={selectAll} className="text-xs">
                      {t('subscriptions.selectAll')}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={clearSelection} className="text-xs">
                      {t('subscriptions.clearAll')}
                    </Button>
                  </div>
                </div>
              </div>

              <div className="p-6">
                {allProfiles.length === 0 ? (
                  <div className="text-center py-12">
                    <CreditCard className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">{t('subscriptions.noProfiles')}</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {allProfiles.map((profile) => {
                      const status = profile._status;
                      const daysLeft = getDaysUntilExpiry(profile.subscription_expires);
                      const isSelected = selectedProfiles.includes(profile.profile_id);
                      
                      return (
                        <div
                          key={profile.profile_id}
                          onClick={() => toggleProfile(profile.profile_id)}
                          className={`flex items-center gap-4 p-4 rounded-xl border cursor-pointer transition-all ${
                            isSelected 
                              ? 'border-blue-500 bg-[#0A2540]/5' 
                              : status === 'expired'
                              ? 'border-red-200 bg-red-50/50'
                              : status === 'expiring'
                              ? 'border-yellow-200 bg-yellow-50/50'
                              : 'border-slate-200 hover:border-slate-300'
                          }`}
                          data-testid={`profile-${profile.profile_id}`}
                        >
                          <Checkbox
                            checked={isSelected}
                            className="data-[state=checked]:bg-[#00C48C]"
                          />
                          
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-mono text-sm font-bold text-[#0A2540]">
                                {profile.profile_id}
                              </span>
                              {status === 'expired' && (
                                <span className="px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-700">
                                  {t('subscriptions.expired')}
                                </span>
                              )}
                              {status === 'expiring' && (
                                <span className="px-2 py-0.5 text-xs rounded-full bg-yellow-100 text-yellow-700">
                                  {daysLeft} {t('subscriptions.daysLeft')}
                                </span>
                              )}
                              {status === 'active' && daysLeft > 15 && (
                                <span className="px-2 py-0.5 text-xs rounded-full bg-[#00C48C]/10 text-[#00A67E]">
                                  {t('subscriptions.active')}
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-slate-600">
                              {profile.first_name} {profile.last_name}
                              {profile.job_title && ` • ${profile.job_title}`}
                            </p>
                          </div>
                          
                          <div className="text-right">
                            <div className="flex items-center gap-2 text-sm text-slate-500">
                              <Calendar className="w-4 h-4" />
                              {formatDate(profile.subscription_expires)}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </motion.div>
          </div>

          {/* Renewal Summary */}
          <div className="lg:col-span-1">
            <motion.div {...fadeUp} className="bg-white rounded-2xl shadow-soft sticky top-24">
              <div className="p-6 border-b border-slate-100">
                <h2 className="font-heading text-xl font-semibold text-slate-900">
                  {t('subscriptions.renewalSummary')}
                </h2>
              </div>
              
              <div className="p-6 space-y-4">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">{t('subscriptions.profilesSelected')}</span>
                  <span className="font-medium">{selectedProfiles.length}</span>
                </div>
                
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">{t('subscriptions.pricePerProfile')}</span>
                  <span className="font-medium">
                    ${subscriptionData?.renewal_cost_per_profile || 0}/{t('subscriptions.year')}
                  </span>
                </div>
                
                <div className="border-t border-slate-100 pt-4">
                  <div className="flex justify-between">
                    <span className="font-semibold text-slate-900">{t('subscriptions.total')}</span>
                    <span className="text-2xl font-bold text-slate-900">
                      ${calculateTotal().toFixed(2)}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    {t('subscriptions.annualSubscription')}
                  </p>
                </div>
                
                <Button
                  className="w-full btn-primary py-6 rounded-xl gap-2"
                  onClick={handleRenew}
                  disabled={selectedProfiles.length === 0 || submitting}
                  data-testid="renew-btn"
                >
                  <Send className="w-5 h-5" />
                  {submitting ? t('common.loading') : t('subscriptions.requestRenewal')}
                </Button>
                
                <p className="text-xs text-center text-slate-500">
                  {t('subscriptions.paymentNote')}
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}
